import { resolveSupportedLanguage } from '@/constants/languages';
import i18n from '@/i18n';
import { ApiError } from '@/utils/apiClient';

/**
 * Why a transcript failed, in the terms the composer needs to explain it.
 *
 * Kept coarse on purpose: a voter who just spoke needs to know whether to try
 * again, move closer to the mic, or type instead — not which HTTP status came
 * back.
 */
export type TranscribeErrorKind =
  'no_speech' | 'too_long' | 'unsupported' | 'rate_limited' | 'network' | 'server_error';

export class TranscribeError extends Error {
  readonly kind: TranscribeErrorKind;

  constructor(kind: TranscribeErrorKind, message?: string) {
    super(message ?? kind);
    this.name = 'TranscribeError';
    this.kind = kind;
  }
}

export interface TranscribeResult {
  text: string;
  durationMs: number;
}

type QueryFn = (endpoint: string, options?: RequestInit) => Promise<Response>;

function kindForStatus(status: number): TranscribeErrorKind {
  switch (status) {
    case 413:
      return 'too_long';
    case 415:
      return 'unsupported';
    case 422:
      return 'no_speech';
    case 429:
      return 'rate_limited';
    default:
      return 'server_error';
  }
}

/**
 * Send a recorded voice note to VoteMate and get the transcript back.
 *
 * The recording is uploaded and discarded — the backend holds it in memory for
 * the request and returns text only.
 *
 * @param audio The recorded blob, in whichever container the browser chose.
 * @param query The app's authenticated fetch wrapper (``useApi``).
 */
export async function transcribeAudio(audio: Blob, query: QueryFn): Promise<TranscribeResult> {
  // Active interface language, canonical-cased via the registry so `zh-Hant`
  // reaches the backend as `zh-Hant` however i18next spells it internally. The
  // backend maps it onto an Azure Speech locale; a correct hint measurably
  // improves short, noisy clips.
  const activeLanguage = resolveSupportedLanguage(i18n.language) ?? i18n.language;

  const form = new FormData();
  // The backend ignores the filename (Azure needs ASCII, and a client-supplied
  // name is untrusted), but FormData requires one for a file part.
  form.append('audio', audio, 'voice-note');
  form.append('locale', activeLanguage);

  let response: Response;
  try {
    response = await query('/chat/transcribe', { method: 'POST', body: form });
  } catch (error) {
    if (error instanceof ApiError) {
      throw new TranscribeError(kindForStatus(error.status));
    }
    throw new TranscribeError('network', (error as Error)?.message);
  }

  const data = await response.json();
  const text = typeof data?.text === 'string' ? data.text.trim() : '';
  if (!text) {
    throw new TranscribeError('no_speech');
  }

  return { text, durationMs: Number(data?.duration_ms) || 0 };
}
